<!-- The MIT License (MIT)

Copyright (c) 2016 NightCatSama.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE. -->
<template>
  <div
    class="vue-slider-component vue-slider-horizontal"
    :class=" this.disabled ? 'vue-slider-disabled' : '' "
    @click="handleClick">

    <div ref="slider" aria-hidden="true" class="vue-slider">

        <div
          id="dot0"
          ref="dot0"
          class="vue-slider-hover vue-slider-dot"
          @mousedown="handleMouseDown($event, 0)"
          @touchstart="handleMouseDown($event, 0)">
          <span class="vue-slider-tooltip-top vue-slider-tooltip-wrap">
            <slot name="tooltip" :value="val[0]" :index="0">
              <span class="vue-slider-tooltip" >{{ formatter ? formatting(val[0]) : val[0] }}</span>
            </slot>
          </span>
        </div>

        <div
          id="dot1"
          ref="dot1"
          class="vue-slider-hover vue-slider-dot"
          @mousedown="handleMouseDown($event, 1)"
          @touchstart="handleMouseDown($event, 1)">
          <span class="vue-slider-tooltip-top vue-slider-tooltip-wrap">
            <slot name="tooltip" :value="val[1]" :index="1">
              <span class="vue-slider-tooltip" >{{ formatter ? formatting(val[1]) : val[1] }}</span>
            </slot>
          </span>
        </div>

      <div ref="process" class="vue-slider-process" ></div>

    </div>
  </div>
</template>

<script>

export default {
  name: 'VueSliderComponent',
  data () {
    return {
      isMoving: false,
      size: 0,
      currentSliderValue: 0,
      currentSlider: 0
    }
  },
  props: {
    data: {
      type: Array,
      default: null
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    interval: {
      type: Number,
      default: 1
    },
    disabled: {
      type: Boolean,
      default: false
    },
    value: {
      type: [String, Number, Array],
      default: 0
    },
    formatter: [String, Function],
  },
  computed: {
    slider () {
      return [this.$refs.dot0, this.$refs.dot1]
    },
    minimum () {
      return this.min
    },
    val: {
      get () {
        return this.currentSliderValue
      },
      set (val) {
        this.currentSliderValue = val
      }
    },
    currentIndex () {
      return [(this.currentSliderValue[0] - this.min), (this.currentSliderValue[1] - this.min)]
    },
    gap () {
      return this.size / (this.max - this.min)
    },
    position () {
      return [(this.currentSliderValue[0] - this.min) * this.gap, (this.currentSliderValue[1] - this.min) * this.gap]
    },
    limit () {
      return [[0, this.position[1]], [this.position[0], this.size]]
    },
    valueLimit () {
      return [[this.min, this.currentSliderValue[1]], [this.currentSliderValue[0], this.max]]
    },
  },
  methods: {
    formatting (value) {
      return typeof this.formatter === 'string'
        ? this.formatter.replace(/\{value\}/, value)
        : this.formatter(value)
    },
    handleClick (e) {
      console.log('handle click')
      // don't fire click events of elemets under slider
      e.stopPropagation()
      if (this.disabled) return

      const pos = e.clientX - this.offset
      const nearestSlider = pos > ((this.position[1] - this.position[0]) / 2 + this.position[0]) ? 1 : 0
      this.currentSlider = nearestSlider
      this.updatePosition(pos)
    },
    handleMouseDown (e, index) {
      console.log(e)
      console.log('handle mouse down')
      if (this.disabled) return

      this.currentSlider = index
      this.isMoving = true
      this.$emit('drag-start', this)
    },
    handleMouseMove (e) {
      if (!this.isMoving) return
      e.preventDefault()

      if (e.targetTouches && e.targetTouches[0]) {
        e = e.targetTouches[0]
      }

      const pos = e.clientX - this.offset
      this.updatePosition(pos, true)
    },
    moveEnd () {
      console.log('handle move end')
      this.isMoving = false
      this.setPosition()
    },
    updatePosition (pos, isDragging) {
      this.$emit('change', this.val)

      const range = this.limit[this.currentSlider]
      const valueRange = this.valueLimit[this.currentSlider]

      if (pos >= range[0] && pos <= range[1]) {
        this.setCurrentSliderTransform(pos)
        const v = Math.round(pos / this.gap) + this.min
        this.setCurrentValue(v, isDragging)
      } else if (pos < range[0]) {
        this.setCurrentSliderTransform(range[0])
        this.setCurrentValue(valueRange[0])
        if (this.currentSlider === 1) {
          this.currentSlider = 0
        }
      } else {
        this.setCurrentSliderTransform(range[1])
        this.setCurrentValue(valueRange[1])
        if (this.currentSlider === 0) {
          this.currentSlider = 1
        }
      }
    },
    setCurrentValue (val, isDragging) {
      if (val < this.min || val > this.max) {
        return false
      }
      this.currentSliderValue.splice(this.currentSlider, 1, val)
      if (!isDragging) {
        this.setPosition()
      }
    },
    setValue (val) {
      if (this.val !== val) {
        const resetVal = this.limitValue(val)
        if (resetVal !== false) {
          this.val = resetVal.concat()
        } else {
          this.val = val.concat()
        }
      }

      this.$nextTick(() => this.setPosition())
    },
    setPosition () {
      this.currentSlider = 0
      this.setCurrentSliderTransform(this.position[this.currentSlider])
      this.currentSlider = 1
      this.setCurrentSliderTransform(this.position[this.currentSlider])
    },
    setCurrentSliderTransform (val) {
      this.slider[this.currentSlider].style.transform = `translateX(${val}px)`
      this.$refs.process.style.width = `${this.currentSlider === 0 ? this.position[1] - val : val - this.position[0]}px`
      this.$refs.process.style.left = `${this.currentSlider === 0 ? val : this.position[0]}px`
    },
    limitValue (val) {
      let insideRange = false
      val = val.map(v => {
        if (v < this.min) {
          insideRange = true
          return this.min
        } else if (v > this.max) {
          insideRange = true
          return this.max
        }
        return v
      })
      return insideRange && val
    },
    getStaticData () {
      if (this.$refs.slider) {
        this.size = this.$refs.slider.offsetWidth
        this.offset = this.$refs.slider.getBoundingClientRect().left
      }
    },
    refresh () {
      if (this.$refs.slider) {
        this.getStaticData()
        this.setPosition()
      }
    }
  },
  mounted () {
    if (typeof window === 'undefined' || typeof document === 'undefined') return

    this.$nextTick(() => {
      this.getStaticData()
      this.setValue(this.value, true, 0)

      document.addEventListener('touchmove', this.handleMouseMove, { passive: false })
      document.addEventListener('touchend', this.moveEnd, { passive: false })
      document.addEventListener('mousemove', this.handleMouseMove)
      document.addEventListener('mouseup', this.moveEnd)
      document.addEventListener('mouseleave', this.moveEnd)

      window.addEventListener('resize', this.refresh)
    })
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.refresh)

    document.removeEventListener('touchmove', this.handleMouseMove)
    document.removeEventListener('touchend', this.moveEnd)
    document.removeEventListener('mousemove', this.handleMouseMove)
    document.removeEventListener('mouseup', this.moveEnd)
    document.removeEventListener('mouseleave', this.moveEnd)
  }
}
</script>

<style lang="scss" scoped>

@import '../_variables.scss';

$height: 32px;

.vue-slider {
  height: $height;
}

.vue-slider-component {
  position: relative;
  box-sizing: border-box;
  user-select: none;
}

.vue-slider-component.vue-slider-disabled {
  opacity: .5;
  cursor: not-allowed;
}

.vue-slider-component.vue-slider-has-label {
  margin-bottom: 15px;
}

.vue-slider-dot {
  top: 0;
  height: 100%;
}

.vue-slider-component.vue-slider-disabled .vue-slider-dot {
  cursor: not-allowed;
}

.vue-slider-component .vue-slider {
  position: relative;
  display: block;
  background-color: #ccc;
}

.vue-slider-component .vue-slider::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
}

.vue-slider-component .vue-slider-process {
  position: absolute;
  background-color: $teal;
  transition: all 0s;
  z-index: 1;
}

.vue-slider-component.vue-slider-horizontal .vue-slider-process {
  width: 0;
  height: 100%;
  top: 0;
  left: 0;
  will-change: width;
}

.vue-slider-component .vue-slider-dot {
  position: absolute;
  background-color: #fff;
  box-shadow: 0.5px 0.5px 2px 1px rgba(0, 0, 0, 0.32);
  transition: all 0s;
  will-change: transform;
  cursor: pointer;
  z-index: 3;
}

.vue-slider-component.vue-slider-horizontal .vue-slider-dot {
  left: 0;
  width: 16px;
}

.vue-slider-component .vue-slider-tooltip-wrap {
  display: none;
  position: absolute;
  z-index: 9;
}

.vue-slider-component .vue-slider-tooltip {
  display: block;
  font-size: 14px;
  white-space: nowrap;
  padding: 2px 5px;
  min-width: 20px;
  text-align: center;
  color: #fff;
  border-radius: 5px;
  border: 1px solid $transparent-gray;
  background-color: $transparent-gray;
}

.vue-slider-component .vue-slider-tooltip-wrap.vue-slider-tooltip-top {
  top: -9px;
  left: 50%;
  transform: translate(-50%, -100%);
}

.vue-slider-component .vue-slider-tooltip-wrap.vue-slider-tooltip-top .vue-slider-tooltip::before {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  width: 0;
  height: 0;
  border: 5px solid transparent;
  border: 6px solid transparent;
  border-top-color: inherit;
  transform: translate(-50%, 0);
}

.vue-slider-component .vue-slider-dot.vue-slider-hover:hover .vue-slider-tooltip-wrap {
  display: block;
}

</style>



// WEBPACK FOOTER //
// vue-slider.vue?48302ec2